"use client";

import { fetchFile } from "@ffmpeg/util";
import {
	AlertTriangle,
	Download,
	Image,
	ImagePlay,
	Loader,
	Play,
	Settings,
	Type,
	Upload,
	Video,
} from "lucide-react"; 
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import VideoPlayer from "@/components/VideoPlayer";
import { useFFmpeg } from "@/contexts/FFmpegContext";
import { FileDropZone } from "@/components/FileDropZone";
import logger from "@/lib/logger";
import { useTranslations } from "next-intl";

// Define watermark types and positions
const WATERMARK_POSITIONS = [
	{ labelKey: "topLeft", value: "10:10" },
	{ labelKey: "topRight", value: "W-w-10:10" },
	{ labelKey: "bottomLeft", value: "10:H-h-10" },
	{ labelKey: "bottomRight", value: "W-w-10:H-h-10" },
	{ labelKey: "center", value: "(W-w)/2:(H-h)/2" },
	{ labelKey: "topCenter", value: "(W-w)/2:10" },
	{ labelKey: "bottomCenter", value: "(W-w)/2:H-h-10" },
];

const OUTPUT_FORMATS = [
	{
		labelKey: "mp4HighQuality",
		ext: "mp4",
		mime: "video/mp4",
		icon: Video,
		command: ["-c:v", "libx264", "-preset", "medium", "-crf", "23"],
	},
	{
		labelKey: "mp4Fast",
		ext: "mp4",
		mime: "video/mp4",
		icon: Video,
		command: ["-c:v", "libx264", "-preset", "fast", "-crf", "25"],
	},
	{
		labelKey: "webmVp9",
		ext: "webm",
		mime: "video/webm",
		icon: Video,
		command: ["-c:v", "libvpx-vp9", "-crf", "30", "-b:v", "0"],
	},
	{
		labelKey: "movQuicktime",
		ext: "mov",
		mime: "video/quicktime",
		icon: Video,
		command: ["-c:v", "libx264", "-preset", "medium", "-crf", "23"],
	},
];

interface WatermarkOptions {
	type: "text" | "image";
	// Text watermark options
	text: string;
	fontSize: number;
	fontColor: string;
	backgroundColor: string;
	backgroundOpacity: number;
	// Image watermark options
	imageFile: File | null;
	imageScale: number;
	// Common options
	position: string;
	opacity: number;
	fadeIn: {
		enabled: boolean;
		duration: number; // seconds
	};
	fadeOut: {
		enabled: boolean;
		duration: number; // seconds
		startTime: number; // seconds from end
	};
}

const DEFAULT_WATERMARK_OPTIONS: WatermarkOptions = {
	type: "text",
	text: "Watermark",
	fontSize: 24,
	fontColor: "#FFFFFF",
	backgroundColor: "#000000",
	backgroundOpacity: 0.5,
	imageFile: null,
	imageScale: 0.15, // 15% of original size
	position: "W-w-10:H-h-10", // Bottom right
	opacity: 0.8,
	fadeIn: {
		enabled: false,
		duration: 2,
	},
	fadeOut: {
		enabled: false,
		duration: 2,
		startTime: 3,
	},
};

const getOptimalThreadCount = (
	performanceMode: "balanced" | "aggressive" = "balanced",
): number => {
	const cores = navigator.hardwareConcurrency || 4;

	if (performanceMode === "aggressive") {
		return cores;
	}

	return Math.max(2, Math.floor(cores * 0.75));
};

const VideoWatermark = () => {
	const t = useTranslations("videoWatermark");
	const { ffmpegInstance, isLoaded, isLoading, hardwareInfo, error, loadFFmpeg } =
		useFFmpeg();

	useEffect(() => {
		loadFFmpeg();
	}, [loadFFmpeg]);

	const [inputFile, setInputFile] = useState<File | null>(null);
	const [selectedFormat, setSelectedFormat] = useState(OUTPUT_FORMATS[0]);
	const [outputURL, setOutputURL] = useState("");
	const [isProcessing, setIsProcessing] = useState(false);
	const [statusLog, setStatusLog] = useState(t("status.initializing"));
	const [progress, setProgress] = useState(0);
	const [startTime, setStartTime] = useState<number | null>(null);
	const [estimatedTimeRemaining, setEstimatedTimeRemaining] =
		useState<string>("");
	const [performanceMode, setPerformanceMode] = useState<
		"balanced" | "aggressive"
	>("balanced");
	const [watermarkOptions, setWatermarkOptions] = useState<WatermarkOptions>(
		DEFAULT_WATERMARK_OPTIONS,
	);
	const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);


	// Helper function to calculate estimated time remaining
	const calculateTimeRemaining = useCallback(
		(currentProgress: number, startTime: number): string => {
			if (currentProgress <= 0) return "";

			const elapsed = Date.now() - startTime;
			const totalEstimated = elapsed / currentProgress;
			const remaining = totalEstimated - elapsed;

			if (remaining <= 0) return t("status.almostDone");

			const minutes = Math.floor(remaining / 60000);
			const seconds = Math.floor((remaining % 60000) / 1000);

			if (minutes > 0) {
				return t("status.minutesRemaining", { minutes, seconds });
			}
			return t("status.secondsRemaining", { seconds });
		},
		[t],
	);

	// Helper function to generate text as PNG image using Canvas
	const generateTextImage = async (options: WatermarkOptions): Promise<File> => {
		return new Promise((resolve, reject) => {
			// Create canvas for text rendering
			const canvas = document.createElement('canvas');
			const ctx = canvas.getContext('2d');
			
			if (!ctx) {
				reject(new Error('Failed to get canvas context'));
				return;
			}

			// Set font properties
			const fontSize = options.fontSize;
			const fontFamily = 'Arial, sans-serif';
			ctx.font = `${fontSize}px ${fontFamily}`;
			
			// Measure text to determine canvas size
			const metrics = ctx.measureText(options.text);
			const textWidth = metrics.width;
			const textHeight = fontSize * 1.2; // Add some padding
			
			// Add padding for background box
			const padding = options.backgroundOpacity > 0 ? 10 : 5;
			canvas.width = textWidth + (padding * 2);
			canvas.height = textHeight + (padding * 2);
			
			// Clear canvas with transparent background
			ctx.clearRect(0, 0, canvas.width, canvas.height);
			
			// Draw background box if enabled
			if (options.backgroundOpacity > 0) {
				ctx.fillStyle = options.backgroundColor;
				ctx.globalAlpha = options.backgroundOpacity;
				ctx.fillRect(0, 0, canvas.width, canvas.height);
				ctx.globalAlpha = 1; // Reset alpha
			}
			
			// Set text properties
			ctx.font = `${fontSize}px ${fontFamily}`;
			ctx.fillStyle = options.fontColor;
			ctx.textAlign = 'left';
			ctx.textBaseline = 'top';
			
			// Apply overall opacity
			if (options.opacity < 1) {
				ctx.globalAlpha = options.opacity;
			}
			
			// Draw the text
			ctx.fillText(options.text, padding, padding);
			
			// Convert canvas to blob and then to File
			canvas.toBlob((blob) => {
				if (blob) {
					const file = new File([blob], 'text_watermark.png', {
						type: 'image/png',
						lastModified: Date.now(),
					});
					resolve(file);
				} else {
					reject(new Error('Failed to generate text image'));
				}
			}, 'image/png', 1.0); // Use maximum quality
		});
	};

	// Helper function to build watermark filter
	const buildWatermarkFilter = (
		options: WatermarkOptions,
	): string[] => {
		const filters: string[] = [];

		// Validate inputs
		if (!options || !options.type) {
			logger.error({ options }, "Invalid watermark options");
			return [];
		}

		if (options.type === "text") {
			// Ensure we have valid text
			if (!options.text || !options.text.trim()) {
				logger.error("Empty text for text watermark");
				return [];
			}

			// For text watermarks, we'll generate an image and use overlay filter
			const textImageFileName = "text_watermark.png";

			// Ensure position is valid - use simple numeric positions for now
			const position = options.position && options.position.trim() ? options.position.trim() : "10:10";

			// Build simple filter chain for text image overlay - no fade effects initially
			const filterChain = `[0:v][1:v]overlay=${position}`;

			filters.push("-i", textImageFileName, "-filter_complex", filterChain);

			logger.debug({ filterChain }, "Text watermark filter chain");
		} else if (options.type === "image" && options.imageFile) {
			// Image watermark using overlay filter - simplified approach
			const imageExtension = options.imageFile.name.split(".").pop() || "png";
			const imageFileName = `watermark.${imageExtension}`;

			// Ensure position is valid
			const position = options.position && options.position.trim() ? options.position.trim() : "10:10";

			// Build simple filter chain for image overlay
			const filterChain = `[0:v][1:v]overlay=${position}`;

			filters.push("-i", imageFileName, "-filter_complex", filterChain);

			logger.debug({ filterChain }, "Image watermark filter chain");
		}

		// Validate the final filters array
		for (let i = 0; i < filters.length; i++) {
			if (filters[i] === "" || filters[i] === null || filters[i] === undefined) {
				logger.error({ index: i, filters }, "Empty filter argument");
				return [];
			}
		}

		return filters;
	};

	// Update status based on FFmpeg loading state
	useEffect(() => {
		if (error) {
			setStatusLog(error);
		} else if (isLoading) {
			setStatusLog(t("status.loading"));
		} else if (isLoaded) {
			setStatusLog(t("status.ready"));
		} else {
			setStatusLog(t("status.initializing"));
		}
	}, [error, isLoading, isLoaded, t]);

	// Set up FFmpeg progress listener
	useEffect(() => {
		if (ffmpegInstance && isProcessing) {
			const handleProgress = ({ progress }: { progress: number }) => {
				setProgress(progress);
				if (startTime) {
					setEstimatedTimeRemaining(
						calculateTimeRemaining(progress, startTime),
					);
				}
			};

			ffmpegInstance.on("progress", handleProgress);

			return () => {
				ffmpegInstance.off("progress", handleProgress);
			};
		}
	}, [ffmpegInstance, isProcessing, startTime, calculateTimeRemaining]);

	// Handle file selection
	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file) {
			setInputFile(file);
			setOutputURL("");
			setStatusLog(
				t("status.fileSelected", { name: file.name }),
			);
		}
	};

	// Helper function to resize image for memory efficiency
	const resizeImageFile = async (file: File, maxWidth: number = 800, maxHeight: number = 600): Promise<File> => {
		return new Promise((resolve) => {
			const canvas = document.createElement('canvas');
			const ctx = canvas.getContext('2d');
			const img = document.createElement('img');
			
			img.onload = () => {
				// Calculate new dimensions while maintaining aspect ratio
				let { width, height } = img;
				
				if (width > maxWidth || height > maxHeight) {
					const aspectRatio = width / height;
					
					if (width > height) {
						width = maxWidth;
						height = width / aspectRatio;
					} else {
						height = maxHeight;
						width = height * aspectRatio;
					}
				}
				
				canvas.width = width;
				canvas.height = height;
				
				// Draw and compress image
				ctx?.drawImage(img, 0, 0, width, height);
				
				canvas.toBlob((blob) => {
					if (blob) {
						const resizedFile = new File([blob], file.name, {
							type: 'image/png', // Use PNG for better quality with transparency
							lastModified: Date.now(),
						});
						resolve(resizedFile);
					} else {
						resolve(file); // Fallback to original file
					}
				}, 'image/png', 0.8);
			};
			
			img.onerror = () => resolve(file); // Fallback to original file
			img.src = URL.createObjectURL(file);
		});
	};

	// Handle watermark image selection
	const handleWatermarkImageChange = async (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const file = event.target.files?.[0];
		if (file) {
			try {
				setStatusLog(t("status.optimizingImage"));
				// Resize image to save memory during FFmpeg processing
				const optimizedFile = await resizeImageFile(file);

				setWatermarkOptions((prev) => ({
					...prev,
					imageFile: optimizedFile,
				}));

				setStatusLog(t("status.imageOptimized"));
			} catch (error) {
				logger.error({ err: error }, "Image optimization failed");
				// Fallback to original file
				setWatermarkOptions((prev) => ({
					...prev,
					imageFile: file,
				}));
				setStatusLog(t("status.imageNotOptimized"));
			}
		}
	};

	// Core watermark processing logic
	const processWatermark = useCallback(async () => {
		if (!inputFile || !ffmpegInstance) return;

		// Validate watermark options
		if (watermarkOptions.type === "text") {
			if (!watermarkOptions.text || !watermarkOptions.text.trim()) {
				setStatusLog(t("errors.emptyText"));
				return;
			}
			// Validate text is not too long (FFmpeg has limits)
			if (watermarkOptions.text.length > 100) {
				setStatusLog(t("errors.textTooLong"));
				return;
			}
		}
		if (watermarkOptions.type === "image" && !watermarkOptions.imageFile) {
			setStatusLog(t("errors.noImage"));
			return;
		}

		// Additional validation for image watermarks
		if (watermarkOptions.type === "image" && watermarkOptions.imageFile) {
			const imageSizeMB = watermarkOptions.imageFile.size / (1024 * 1024);
			
			// Warn about large images
			if (imageSizeMB > 5) {
				setStatusLog(t("errors.largeImageWarning"));
			}

			// Check for memory-intensive combinations
			const videoSizeMB = inputFile.size / (1024 * 1024);
			if (hardwareInfo.isLowEnd && (videoSizeMB > 50 || imageSizeMB > 2)) {
				setStatusLog(t("errors.lowEndWarning"));
			}
		}

		setIsProcessing(true);
		setOutputURL("");
		setProgress(0);
		setStartTime(Date.now());
		setEstimatedTimeRemaining("");
		setStatusLog(t("status.addingWatermark", { name: inputFile.name }));

		const inputFileName = `input.${inputFile.name.split(".").pop()}`;
		const outputFileName = `watermarked.${selectedFormat.ext}`;

		try {
			// Check file size
			const fileSizeMB = inputFile.size / (1024 * 1024);
			const memoryLimitMB = hardwareInfo.memory * 1024;

			if (hardwareInfo.isLowEnd && fileSizeMB > memoryLimitMB * 0.3) {
				throw new Error(
					t("errors.fileTooLarge", { size: fileSizeMB.toFixed(1) }),
				);
			}

			setStatusLog(t("status.loadingFile"));

			// Write input video to FFmpeg filesystem
			try {
				await ffmpegInstance.writeFile(
					inputFileName,
					await fetchFile(inputFile),
				);
			} catch (writeError) {
				if (
					writeError instanceof Error &&
					writeError.message.includes("memory")
				) {
					throw new Error(
						t("errors.outOfMemoryLoad"),
					);
				}
				throw writeError;
			}

			// Handle watermark file preparation
			if (watermarkOptions.type === "text") {
				// Generate text watermark as PNG image
				setStatusLog(t("status.generatingText"));
				try {
					const textImageFile = await generateTextImage(watermarkOptions);
					logger.debug({ size: textImageFile.size, type: textImageFile.type }, "Generated text image");
					
					// Convert to ArrayBuffer for FFmpeg
					const textImageBuffer = await fetchFile(textImageFile);
					logger.debug({ byteLength: textImageBuffer.byteLength }, "Text image buffer size");
					
					// Write to FFmpeg filesystem
					await ffmpegInstance.writeFile("text_watermark.png", textImageBuffer);
					logger.debug("Text watermark written to FFmpeg filesystem");
					
					// Verify the file exists in FFmpeg filesystem
					try {
						const fileList = await ffmpegInstance.listDir('/');
						logger.debug({ fileList }, "FFmpeg filesystem contents");
					} catch (listError) {
						logger.debug({ err: listError }, "Could not list FFmpeg filesystem");
					}
				} catch (textError) {
					logger.error({ err: textError }, "Text watermark generation error");
					throw new Error("Failed to generate text watermark image: " + (textError instanceof Error ? textError.message : 'Unknown error'));
				}
			} else if (watermarkOptions.type === "image" && watermarkOptions.imageFile) {
				// Load image watermark
				setStatusLog(t("status.loadingImage"));
				const imageFileName = `watermark.${watermarkOptions.imageFile.name.split(".").pop()}`;
				await ffmpegInstance.writeFile(
					imageFileName,
					await fetchFile(watermarkOptions.imageFile),
				);
			}

			// Build memory-optimized command with threading
			let threadCount = getOptimalThreadCount(performanceMode);

			// Force single-threaded for image watermarks on low-end devices to prevent OOM
			if (watermarkOptions.type === "image" && hardwareInfo.isLowEnd) {
				threadCount = 1;
			}

			if (!hardwareInfo.supportsSharedArrayBuffer) {
				threadCount = 1;
			}

			const baseCommand = ["-i", inputFileName];
			const watermarkFilters = buildWatermarkFilter(watermarkOptions);

			// Validate that we have a proper filter
			if (watermarkFilters.length === 0) {
				throw new Error(t("errors.filterFailed"));
			}

			// Memory optimization settings
			const memoryOptimizations = [
				"-avoid_negative_ts", "make_zero",  // Avoid memory issues with timestamps
				"-fflags", "+genpts",               // Generate presentation timestamps
				"-max_muxing_queue_size", "1024",   // Limit muxing queue to save memory
			];

			// CPU optimizations for libx264
			const cpuOptimizations = [];
			if (selectedFormat.command.includes("libx264")) {
				cpuOptimizations.push(
					"-x264-params", "nal-hrd=cbr",    // Better memory management
					"-movflags", "+faststart",         // Fast start for web playback
				);
				
				// Add CPU capability settings based on detected architecture
				if (hardwareInfo.detectedArchitecture === 'arm64') {
					// Optimize for Apple Silicon
					cpuOptimizations.push("-tune", "fastdecode");
				} else {
					// Standard x86_64 optimizations
					cpuOptimizations.push("-tune", "film");
				}
			}

			// Build final command with optimizations
			const fullCommand = [
				...baseCommand,
				...watermarkFilters,
				...memoryOptimizations,
				...selectedFormat.command,
				...cpuOptimizations,
				"-threads", threadCount.toString(),
				outputFileName,
			];

			setStatusLog(t("status.processing", { threads: threadCount.toString() }));

			logger.debug({ fullCommand }, "Watermark command");
			logger.debug({ watermarkFilters }, "Watermark filters");
			logger.debug({ watermarkOptions }, "Watermark options");

			// Additional debug: check for empty string arguments
			const emptyArgs = fullCommand.filter((arg, index) => arg === "" && index > 0);
			if (emptyArgs.length > 0) {
				logger.error({ fullCommand }, "Found empty string arguments in command");
				throw new Error(t("errors.invalidCommand"));
			}

			try {
				await ffmpegInstance.exec(fullCommand);
			} catch (execError) {
				let errorMessage = "";
				const errorStr = execError instanceof Error ? execError.message : String(execError);
				
				if (errorStr.includes("memory") || errorStr.includes("Out of memory") || errorStr.includes("RangeError")) {
					errorMessage = t("errors.outOfMemoryProcess");
				} else if (errorStr.includes("Aborted(OOM)")) {
					errorMessage = t("errors.outOfMemoryWasm");
				} else if (errorStr.includes("cpu capabilities: none")) {
					errorMessage = t("errors.cpuRetry");
					
					// Retry with simplified command (remove CPU optimizations)
					try {
						const fallbackCommand = [
							...baseCommand,
							...watermarkFilters,
							...memoryOptimizations,
							...selectedFormat.command.filter(arg => !arg.includes("tune") && !arg.includes("preset")),
							"-preset", "ultrafast",  // Use fastest preset
							"-threads", "1",         // Force single-threaded
							outputFileName,
						];
						
						logger.debug({ fallbackCommand }, "Retrying with fallback command");
						setStatusLog(t("status.retrying"));
						await ffmpegInstance.exec(fallbackCommand);
						
						// If successful, continue to read output
					} catch {
						throw new Error(t("errors.compatibilityFailed"));
					}
				} else if (errorStr.includes("drawtext") || errorStr.includes("text") || errorStr.includes("font")) {
					errorMessage = t("errors.textError");
				} else if (errorStr.includes("Invalid") && watermarkOptions.type === "text") {
					errorMessage = t("errors.invalidTextSettings");
				} else {
					errorMessage = t("errors.processingFailed", { error: errorStr });
				}
				
				if (errorMessage && !errorStr.includes("cpu capabilities: none")) {
					throw new Error(errorMessage);
				}
			}

			setStatusLog(t("status.readingFile"));

			// Read output file
			let data: Uint8Array;
			try {
				data = (await ffmpegInstance.readFile(outputFileName)) as Uint8Array;
			} catch (readError) {
				if (
					readError instanceof Error &&
					readError.message.includes("memory")
				) {
					throw new Error(
						t("errors.outOfMemoryRead"),
					);
				}
				throw readError;
			}

			// Create downloadable blob
			try {
				const blob = new Blob([data as BlobPart], {
					type: selectedFormat.mime,
				});
				const url = URL.createObjectURL(blob);
				setOutputURL(url);
				setProgress(1);
				setEstimatedTimeRemaining("");

				setStatusLog(
					t("status.complete", { format: selectedFormat.ext.toUpperCase() }),
				);
			} catch {
				throw new Error(
					t("errors.outOfMemoryDownload"),
				);
			}
		} catch (error) {
			const errorMessage =
				error instanceof Error ? error.message : t("errors.unknown");
			setStatusLog(t("status.failed", { error: errorMessage }));
			logger.error({ err: error }, "FFmpeg Watermark Error");
			setProgress(0);
			setEstimatedTimeRemaining("");
		} finally {
			setIsProcessing(false);
			// Cleanup filesystem
			try {
				await ffmpegInstance.deleteFile(inputFileName);
				await ffmpegInstance.deleteFile(outputFileName);
				
				if (watermarkOptions.type === "text") {
					await ffmpegInstance.deleteFile("text_watermark.png");
				} else if (watermarkOptions.type === "image" && watermarkOptions.imageFile) {
					const imageFileName = `watermark.${watermarkOptions.imageFile.name.split(".").pop()}`;
					await ffmpegInstance.deleteFile(imageFileName);
				}
			} catch {
				// Files may not exist, ignore cleanup errors
			}
		}
	}, [
		inputFile,
		ffmpegInstance,
		selectedFormat,
		performanceMode,
		hardwareInfo,
		watermarkOptions,
		t,
	]);

	// Helper component to display the selected format icon
	const FormatIcon = selectedFormat.icon;

	// Show loading spinner until component is mounted

	return (
		<div className="max-w-7xl mx-auto min-h-screen p-4 sm:p-8">
			<div className="mx-auto p-2 sm:p-6 md:p-10">
				<header className="text-center mb-10">
					<h1 className="text-3xl sm:text-4xl font-black flex items-center justify-center uppercase tracking-wide">
						<ImagePlay
							className="h-8 w-8 me-4 text-destructive"
							aria-hidden="true"
						/>
						{t("title")}
					</h1>
					<p className="mt-4 text-muted-foreground text-lg font-bold">
						{t("subtitle")}
					</p>
				</header>

				{/* Status Bar */}
				<Alert
					className={`mb-6 ${isLoaded ? "border-green-500 bg-green-50" : "border-yellow-500 bg-yellow-50"}`}
				>
					<AlertTriangle className="h-4 w-4" aria-hidden="true" />
					<AlertDescription className="font-bold">{statusLog}</AlertDescription>
				</Alert>

				{/* Main Interface */}
				<div className="flex flex-col gap-6">
					{/* 1. Upload Section */}
					<Card className="p-3 sm:p-6">
						<CardHeader className="p-0 mb-4">
							<CardTitle className="flex items-center gap-3">
								<Upload className="w-6 h-6" aria-hidden="true" />
								{t("steps.selectFile")}
							</CardTitle>
						</CardHeader>
						<CardContent className="p-0">
							<FileDropZone accept="video/*" disabled={!isLoaded || isProcessing} onFiles={(files) => handleFileChange({ target: { files } } as any)} label={t("fileInput.dropLabel")} activeLabel={t("fileInput.activeLabel")} />
							{inputFile && (
								<p id="file-info" className="mt-3 text-sm font-bold">
									<span className="text-primary">{inputFile.name}</span> (
									{(inputFile.size / 1024 / 1024).toFixed(2)} MB)
								</p>
							)}
							{!isLoaded && (
								<div className="mt-3 text-yellow-600 flex items-center font-bold">
									<Loader
										className="w-4 h-4 me-2 animate-spin"
										aria-hidden="true"
									/>
									{t("fileInput.loading")}
								</div>
							)}
							{!inputFile && isLoaded && (
								<p
									id="file-help"
									className="mt-2 text-sm text-muted-foreground font-bold"
								>
									{t("fileInput.hint")}
								</p>
							)}
						</CardContent>
					</Card>

					{/* 2. Watermark Configuration */}
					<Card className="p-3 sm:p-6">
						<CardHeader className="p-0 mb-4">
							<CardTitle className="flex items-center gap-3">
								<Settings className="w-6 h-6" aria-hidden="true" />
								{t("steps.configureWatermark")}
							</CardTitle>
						</CardHeader>
						<CardContent className="p-0 space-y-6">
							{/* Watermark Type Selection */}
							<div>
								<Label className="text-lg font-black mb-4 block">
									{t("watermarkConfig.typeLabel")}
								</Label>
								<div className="grid grid-cols-2 gap-4" role="radiogroup" aria-label={t("watermarkConfig.typeAriaLabel")}>
									<Button
										variant={
											watermarkOptions.type === "text" ? "default" : "outline"
										}
										onClick={() =>
											setWatermarkOptions((prev) => ({ ...prev, type: "text" }))
										}
										disabled={isProcessing}
										className="h-auto p-4 flex-col gap-2"
										role="radio"
										aria-checked={watermarkOptions.type === "text"}
									>
										<Type className="w-6 h-6" aria-hidden="true" />
										<div className="font-black">{t("watermarkConfig.textWatermark")}</div>
									</Button>
									<Button
										variant={
											watermarkOptions.type === "image" ? "default" : "outline"
										}
										onClick={() =>
											setWatermarkOptions((prev) => ({
												...prev,
												type: "image",
											}))
										}
										disabled={isProcessing}
										className="h-auto p-4 flex-col gap-2"
										role="radio"
										aria-checked={watermarkOptions.type === "image"}
									>
										<Image className="w-6 h-6" aria-hidden="true" />
										<div className="font-black">{t("watermarkConfig.imageWatermark")}</div>
									</Button>
								</div>
							</div>

							{/* Text Watermark Options */}
							{watermarkOptions.type === "text" && (
								<div className="space-y-4 p-4">
									<h4 className="font-black text-md">{t("watermarkConfig.textSettings")}</h4>

									{/* Text Input */}
									<div>
										<Label htmlFor="watermark-text">{t("watermarkConfig.watermarkTextLabel")}</Label>
										<input
											id="watermark-text"
											type="text"
											value={watermarkOptions.text}
											onChange={(e) =>
												setWatermarkOptions((prev) => ({
													...prev,
													text: e.target.value,
												}))
											}
											className="neo-input w-full mt-1"
											placeholder={t("watermarkConfig.textPlaceholder")}
											disabled={isProcessing}
										/>
									</div>

									{/* Font Size */}
									<div>
										<Label className="text-xs text-muted-foreground">
											{t("watermarkConfig.fontSize", { value: watermarkOptions.fontSize })}
										</Label>
										<Slider
											value={[watermarkOptions.fontSize]}
											onValueChange={([value]) =>
												setWatermarkOptions((prev) => ({
													...prev,
													fontSize: value,
												}))
											}
											min={12}
											max={72}
											step={2}
											className="mt-1"
										/>
									</div>

									{/* Font Color */}
									<div>
										<Label htmlFor="font-color">{t("watermarkConfig.fontColorLabel")}</Label>
										<input
											id="font-color"
											type="color"
											value={watermarkOptions.fontColor}
											onChange={(e) =>
												setWatermarkOptions((prev) => ({
													...prev,
													fontColor: e.target.value,
												}))
											}
											className="w-full h-10 mt-1 rounded border"
											disabled={isProcessing}
										/>
									</div>

									{/* Background */}
									<div className="space-y-3">
										<div>
											<Label htmlFor="bg-color">{t("watermarkConfig.bgColorLabel")}</Label>
											<input
												id="bg-color"
												type="color"
												value={watermarkOptions.backgroundColor}
												onChange={(e) =>
													setWatermarkOptions((prev) => ({
														...prev,
														backgroundColor: e.target.value,
													}))
												}
												className="w-full h-10 mt-1 rounded border"
												disabled={isProcessing}
											/>
										</div>
										<div>
											<Label className="text-xs text-muted-foreground">
												{t("watermarkConfig.bgOpacity", { value: Math.round(watermarkOptions.backgroundOpacity * 100) })}
											</Label>
											<Slider
												value={[watermarkOptions.backgroundOpacity]}
												onValueChange={([value]) =>
													setWatermarkOptions((prev) => ({
														...prev,
														backgroundOpacity: value,
													}))
												}
												min={0}
												max={1}
												step={0.1}
												className="mt-1"
											/>
										</div>
									</div>
								</div>
							)}

							{/* Image Watermark Options */}
							{watermarkOptions.type === "image" && (
								<div className="space-y-4 p-4 border rounded-lg">
									<h4 className="font-black text-md">{t("watermarkConfig.imageSettings")}</h4>

									{/* Image Upload */}
									<div>
										<Label htmlFor="watermark-image">{t("watermarkConfig.watermarkImageLabel")}</Label>
										<FileDropZone accept="image/*" disabled={isProcessing} onFiles={(files) => { const f = Array.from(files)[0]; if (f) handleWatermarkImageChange({ target: { files } } as any); }} label={t("watermarkConfig.imageDropLabel")} activeLabel={t("watermarkConfig.imageActiveLabel")} />
										{watermarkOptions.imageFile && (
											<p className="mt-2 text-sm text-muted-foreground">
												{t("watermarkConfig.imageSelected", { name: watermarkOptions.imageFile.name })}
											</p>
										)}
									</div>

									{/* Image Scale */}
									<div>
										<Label className="text-xs text-muted-foreground">
											{t("watermarkConfig.imageScale", { value: Math.round(watermarkOptions.imageScale * 100) })}
										</Label>
										<Slider
											value={[watermarkOptions.imageScale]}
											onValueChange={([value]) =>
												setWatermarkOptions((prev) => ({
													...prev,
													imageScale: value,
												}))
											}
											min={0.05}
											max={0.5}
											step={0.01}
											className="mt-1"
										/>
										<div className="flex justify-between text-xs text-muted-foreground mt-1">
											<span>{t("watermarkConfig.imageScaleMin")}</span>
											<span>{t("watermarkConfig.imageScaleMax")}</span>
										</div>
									</div>
								</div>
							)}

							{/* Position */}
							<div>
								<Label className="text-lg font-black mb-3 block">
									{t("watermarkConfig.positionLabel")}
								</Label>
								<Select
									value={watermarkOptions.position}
									onValueChange={(value) =>
										setWatermarkOptions((prev) => ({
											...prev,
											position: value,
										}))
									}
									disabled={isProcessing}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{WATERMARK_POSITIONS.map((pos) => (
											<SelectItem key={pos.value} value={pos.value}>
												{t(`watermarkConfig.positions.${pos.labelKey}`)}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							{/* Opacity */}
							<div>
								<Label className="text-xs text-muted-foreground">
									{t("watermarkConfig.opacity", { value: Math.round(watermarkOptions.opacity * 100) })}
								</Label>
								<Slider
									value={[watermarkOptions.opacity]}
									onValueChange={([value]) =>
										setWatermarkOptions((prev) => ({ ...prev, opacity: value }))
									}
									min={0.1}
									max={1}
									step={0.1}
									className="mt-1"
								/>
							</div>

							{/* Advanced Options */}
							<div>
								<Button
									variant="outline"
									onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
									className="w-full"
									aria-expanded={showAdvancedOptions}
								>
									{showAdvancedOptions ? t("watermarkConfig.hideAdvanced") : t("watermarkConfig.showAdvanced")}
								</Button>
							</div>

							{showAdvancedOptions && (
								<div className="space-y-4 p-4 border rounded-lg">
									{/* Fade In */}
									<div className="space-y-3">
										<div className="flex items-center space-x-2">
											<Checkbox
												id="fade-in"
												checked={watermarkOptions.fadeIn.enabled}
												onCheckedChange={(checked) =>
													setWatermarkOptions((prev) => ({
														...prev,
														fadeIn: { ...prev.fadeIn, enabled: !!checked },
													}))
												}
											/>
											<Label htmlFor="fade-in" className="text-sm font-medium">
												{t("watermarkConfig.fadeInLabel")}
											</Label>
										</div>
										{watermarkOptions.fadeIn.enabled && (
											<div className="ms-6">
												<Label className="text-xs text-muted-foreground">
													{t("watermarkConfig.fadeInDuration", { value: watermarkOptions.fadeIn.duration })}
												</Label>
												<Slider
													value={[watermarkOptions.fadeIn.duration]}
													onValueChange={([value]) =>
														setWatermarkOptions((prev) => ({
															...prev,
															fadeIn: { ...prev.fadeIn, duration: value },
														}))
													}
													min={0.5}
													max={10}
													step={0.5}
													className="mt-1"
												/>
											</div>
										)}
									</div>

									{/* Fade Out */}
									<div className="space-y-3">
										<div className="flex items-center space-x-2">
											<Checkbox
												id="fade-out"
												checked={watermarkOptions.fadeOut.enabled}
												onCheckedChange={(checked) =>
													setWatermarkOptions((prev) => ({
														...prev,
														fadeOut: { ...prev.fadeOut, enabled: !!checked },
													}))
												}
											/>
											<Label htmlFor="fade-out" className="text-sm font-medium">
												{t("watermarkConfig.fadeOutLabel")}
											</Label>
										</div>
										{watermarkOptions.fadeOut.enabled && (
											<div className="ms-6 space-y-3">
												<div>
													<Label className="text-xs text-muted-foreground">
														{t("watermarkConfig.fadeOutDuration", { value: watermarkOptions.fadeOut.duration })}
													</Label>
													<Slider
														value={[watermarkOptions.fadeOut.duration]}
														onValueChange={([value]) =>
															setWatermarkOptions((prev) => ({
																...prev,
																fadeOut: { ...prev.fadeOut, duration: value },
															}))
														}
														min={0.5}
														max={10}
														step={0.5}
														className="mt-1"
													/>
												</div>
												<div>
													<Label className="text-xs text-muted-foreground">
														{t("watermarkConfig.fadeOutStartTime", { value: watermarkOptions.fadeOut.startTime })}
													</Label>
													<Slider
														value={[watermarkOptions.fadeOut.startTime]}
														onValueChange={([value]) =>
															setWatermarkOptions((prev) => ({
																...prev,
																fadeOut: { ...prev.fadeOut, startTime: value },
															}))
														}
														min={1}
														max={30}
														step={1}
														className="mt-1"
													/>
												</div>
											</div>
										)}
									</div>
								</div>
							)}
						</CardContent>
					</Card>

					{/* 3. Output Format Selection */}
					<Card className="p-3 sm:p-6">
						<CardHeader className="p-0 mb-4">
							<CardTitle className="flex items-center gap-3">
								<FormatIcon className="w-6 h-6" aria-hidden="true" />
								{t("steps.selectFormat")}
							</CardTitle>
						</CardHeader>
						<CardContent className="p-0">
							<Select
								value={OUTPUT_FORMATS.indexOf(selectedFormat).toString()}
								onValueChange={(value) => {
									const formatIndex = parseInt(value);
									const format = OUTPUT_FORMATS[formatIndex];
									if (format) {
										setSelectedFormat(format);
										setOutputURL("");
									}
								}}
								disabled={!isLoaded || isProcessing}
							>
								<SelectTrigger className="w-full">
									<SelectValue placeholder={t("formats.placeholder")} />
								</SelectTrigger>
								<SelectContent>
									{OUTPUT_FORMATS.map((format, idx) => (
										<SelectItem key={idx} value={idx.toString()}>
											{t(`formats.${format.labelKey}`)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<p className="mt-2 text-sm text-muted-foreground font-bold">
								{t("formats.selected", { label: t(`formats.${selectedFormat.labelKey}`) })}
							</p>

							{/* Performance Mode */}
							<div className="mt-6">
								<h3 className="text-lg font-black mb-4">{t("performance.title")}</h3>
								<div className="grid grid-cols-2 gap-4" role="radiogroup" aria-label={t("performance.performanceAriaLabel")}>
									<Button
										variant={
											performanceMode === "balanced" ? "default" : "outline"
										}
										onClick={() => setPerformanceMode("balanced")}
										disabled={isProcessing}
										className="h-auto p-4 flex-col gap-2"
										role="radio"
										aria-checked={performanceMode === "balanced"}
									>
										<div className="font-black">{t("performance.balanced")}</div>
										<div className="text-xs opacity-75">
											{t("performance.cores", { used: hardwareInfo.balancedThreads, total: hardwareInfo.cores })}
										</div>
									</Button>
									<Button
										variant={
											performanceMode === "aggressive"
												? "destructive"
												: "outline"
										}
										onClick={() => setPerformanceMode("aggressive")}
										disabled={isProcessing}
										className="h-auto p-4 flex-col gap-2"
										role="radio"
										aria-checked={performanceMode === "aggressive"}
									>
										<div className="font-black">{t("performance.maximum")}</div>
										<div className="text-xs opacity-75">
											{t("performance.cores", { used: hardwareInfo.aggressiveThreads, total: hardwareInfo.cores })}
										</div>
									</Button>
								</div>
							</div>
						</CardContent>
					</Card>

					{/* 4. Process Button */}
					<div className="flex justify-center">
						<Button
							onClick={processWatermark}
							size="lg"
							variant={
								isLoaded && inputFile && !isProcessing ? "default" : "outline"
							}
							disabled={!isLoaded || !inputFile || isProcessing}
							className="px-8 py-4 text-lg"
						>
							{isProcessing ? (
								<>
									<Loader
										className="w-5 h-5 me-3 animate-spin"
										aria-hidden="true"
									/>
									{t("convert.addingWatermark")}
								</>
							) : (
								<>
									<Play className="w-5 h-5 me-3" aria-hidden="true" />
									{t("convert.addWatermark")}
								</>
							)}
						</Button>
					</div>

					{/* Progress Bar */}
					{isProcessing && (
						<Card className="mb-6 p-3 sm:p-6">
							<div className="flex justify-between items-center mb-4">
								<Badge variant="secondary" className="font-black text-lg">
									{t("progress.addingWatermarkTo", { format: selectedFormat.ext.toUpperCase() })}
								</Badge>
								<span className="text-lg font-black">
									{Math.round(progress * 100)}%
								</span>
							</div>
							<Progress
								value={progress * 100}
								className="mb-4"
								aria-label={t("progress.progressLabel", { percent: Math.round(progress * 100) })}
							/>
							{estimatedTimeRemaining && (
								<div className="text-sm font-bold text-center text-muted-foreground">
									{estimatedTimeRemaining}
								</div>
							)}
						</Card>
					)}
					{/* 5. Preview & Download Section */}
					<Card className="p-3 sm:p-6">
						<CardHeader className="p-0 mb-4">
							<CardTitle className="flex items-center gap-3">
								<Download className="w-6 h-6" aria-hidden="true" />
								{t("steps.previewDownload")}
							</CardTitle>
						</CardHeader>
						<CardContent className="p-0">
							{outputURL ? (
								<div className="space-y-6">
									{/* Video Preview */}
									<div>
										<h4 className="text-md font-bold mb-3">{t("download.videoPreview")}</h4>
										<VideoPlayer
											videoUrl={outputURL}
											fileName={`watermarked_video.${selectedFormat.ext}`}
										/>
									</div>

									{/* Download Section */}
									<div className="flex flex-col items-center justify-center text-center space-y-4 pt-4 border-t">
										<Badge
											variant="secondary"
											className="text-lg font-black px-4 py-2"
										>
											{t("download.completed")}
										</Badge>
										<Button asChild size="lg" className="px-6 py-3">
											<a
												href={outputURL}
												download={`watermarked_video.${selectedFormat.ext}`}
												aria-describedby="download-info"
											>
												<Download className="w-5 h-5 me-3" aria-hidden="true" />
												{t("download.downloadFile", { ext: selectedFormat.ext })}
											</a>
										</Button>
										<p
											id="download-info"
											className="text-sm text-muted-foreground font-bold"
										>
											{t("download.readyForDownload")}
										</p>
									</div>
								</div>
							) : (
								<div className="text-center min-h-[12rem] flex items-center justify-center">
									{isProcessing ? (
										<p className="text-primary font-bold text-lg">
											{t("download.processingMessage")}
										</p>
									) : (
										<p className="text-muted-foreground font-bold">
											{t("download.emptyMessage")}
										</p>
									)}
								</div>
							)}
						</CardContent>
					</Card>
				</div>

				{/* Information */}
				<Alert className="mt-8">
					<AlertTriangle className="h-4 w-4 text-green-600" aria-hidden="true" />
					<AlertDescription className="font-bold">
						{t.rich("privacy", {
							code: (chunks) => (
								<code className="bg-muted p-1 rounded font-mono text-sm">
									{chunks}
								</code>
							),
						})}
					</AlertDescription>
				</Alert>
			</div>
		</div>
	);
};

export default VideoWatermark;
